import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { hasRole } from '@/utils/roles';
import { learnerUrl } from '@/config/portals';

interface SuperAdminGateProps {
  children: ReactNode;
}

/**
 * Guards the super-admin platform console (mounted on the `admin.` host —
 * see App.tsx). Three states:
 *  - unauthenticated -> redirect to /login, preserving the deep link
 *  - authenticated without SUPER_ADMIN -> full-page "no access" screen with
 *    a link back to the main (learner) host
 *  - SUPER_ADMIN -> renders children
 */
export default function SuperAdminGate({ children }: SuperAdminGateProps) {
  const location = useLocation();
  const { isAuthenticated, isAuthReady, isLoading, user } = useAuthStore();

  // Mirror ProtectedRoute: don't flash a redirect/no-access screen while auth
  // is still being validated or rehydrated.
  if (isLoading || !isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-tutara-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasRole(user?.roles, 'SUPER_ADMIN')) {
    const mainHost = learnerUrl();
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
        <h1 className="text-2xl font-bold text-tutara-charcoal dark:text-white mb-2">
          You don't have access to the platform console
        </h1>
        <p className="text-tutara-gray dark:text-gray-400 mb-6 max-w-md">
          This area is restricted to Tutara platform administrators. If you
          believe this is a mistake, contact support.
        </p>
        <a
          href={mainHost.href}
          data-testid="superadmin-gate-main-host-link"
          className="text-tutara-primary hover:text-tutara-primary-dark font-semibold transition-colors"
        >
          Return to Tutara
        </a>
      </div>
    );
  }

  return <>{children}</>;
}
